import { isAxiosError } from 'axios'
import {
  configureMdmFetch,
  type MdmFetchResponse,
} from '@os/mdm/web/services/mdmFetch.js'
import { lazy, Suspense, useLayoutEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import AppLayout from '../../components/layout/AppLayout'
import { useActiveServer } from '../../hooks/useActiveServer'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

const MdmOverviewPage = lazy(() => import('@os/mdm/web/pages/MdmOverviewPage.js'))
const DevicesPage = lazy(() => import('@os/mdm/web/pages/DevicesPage.js'))
const PoliciesPage = lazy(() => import('@os/mdm/web/pages/PoliciesPage.js'))
const ApplicationsPage = lazy(() => import('@os/mdm/web/pages/ApplicationsPage.js'))
const LocalAccessPage = lazy(() => import('@os/mdm/web/pages/LocalAccessPage.js'))
const CommandsPage = lazy(() => import('@os/mdm/web/pages/CommandsPage.js'))
const EnrollmentPage = lazy(() => import('@os/mdm/web/pages/EnrollmentPage.js'))
const CompliancePage = lazy(() => import('@os/mdm/web/pages/CompliancePage.js'))
const SettingsPage = lazy(() => import('@os/mdm/web/pages/SettingsPage.js'))

function responseFrom(status: number, data: unknown): MdmFetchResponse {
  return {
    ok: status >= 200 && status < 300,
    status,
    json: async () => data,
    text: async () => {
      if (typeof data === 'string') return data
      if (data === undefined) return ''
      try {
        return JSON.stringify(data) ?? ''
      } catch {
        return ''
      }
    },
  }
}

/**
 * Fetch-compatible adapter over the shell's authenticated Axios client.
 * Axios retains access-token refresh and client identity headers; the explicit
 * server header pins every MDM request to the same tenant as the scope key.
 */
export async function mdmApiFetch(
  serverId: string,
  input: string,
  init?: RequestInit,
): Promise<MdmFetchResponse> {
  const headers = new Headers(init?.headers)
  headers.set('X-Server-Id', serverId)

  try {
    const response = await api.request({
      url: input.replace(/^\/api(?=\/)/, ''),
      method: init?.method ?? 'GET',
      headers: Object.fromEntries(headers.entries()),
      data: init?.body,
      signal: init?.signal ?? undefined,
    })
    return responseFrom(response.status, response.data)
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return responseFrom(error.response.status, error.response.data)
    }
    throw error
  }
}

function MdmRoute({ segment }: { segment: string }) {
  let page: ReactNode
  switch (segment) {
    case 'devices': page = <DevicesPage />; break
    case 'policies': page = <PoliciesPage />; break
    case 'applications': page = <ApplicationsPage />; break
    case 'local-access': page = <LocalAccessPage />; break
    case 'commands': page = <CommandsPage />; break
    case 'enrollment': page = <EnrollmentPage />; break
    case 'compliance': page = <CompliancePage />; break
    case 'settings': page = <SettingsPage />; break
    default: page = <MdmOverviewPage />
  }

  return (
    <Suspense
      fallback={(
        <main className="flex min-h-0 flex-1 items-center justify-center p-6" role="status">
          Loading Device Management workspace…
        </main>
      )}
    >
      {page}
    </Suspense>
  )
}

export default function MdmWorkspacePage() {
  const location = useLocation()
  const activeServer = useActiveServer()
  const userId = useAuthStore((state) => state.user?.id)
  const serverId = activeServer?.id
  const enabledModules = activeServer?.enabledModules
  const enabled = Boolean(
    enabledModules
    && typeof enabledModules === 'object'
    && !Array.isArray(enabledModules)
    && (enabledModules as Record<string, unknown>).mdm === true,
  )
  const segment = location.pathname.slice('/workspace/mdm'.length).split('/').filter(Boolean)[0] ?? ''

  useLayoutEffect(() => {
    if (!serverId || !userId || !enabled) {
      configureMdmFetch(null)
      return () => configureMdmFetch(null)
    }

    configureMdmFetch({
      scopeKey: `mdm:${serverId}:${userId}`,
      fetcher: (input, init) => mdmApiFetch(serverId, input, init),
    })
    return () => configureMdmFetch(null)
  }, [enabled, serverId, userId])

  return (
    <AppLayout>
      {!activeServer ? (
        <main
          className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6"
          role="status"
          aria-labelledby="mdm-workspace-state"
        >
          <div className="max-w-lg rounded-xl border border-border bg-card p-6 text-center">
            <h1 id="mdm-workspace-state" className="text-xl font-semibold">
              Select a workspace to open Device Management
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Corporate device inventory and commands always belong to one organization.
            </p>
          </div>
        </main>
      ) : !enabled ? (
        <main
          className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6"
          role="status"
          aria-labelledby="mdm-workspace-state"
        >
          <div className="max-w-lg rounded-xl border border-border bg-card p-6 text-center">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              ViewSX Device Management
            </p>
            <h1 id="mdm-workspace-state" className="mt-2 text-xl font-semibold">
              Device Management is not enabled for this workspace
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Ask a workspace owner to enable the organization’s Device Management subscription.
            </p>
          </div>
        </main>
      ) : !userId ? (
        <main
          className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6"
          role="status"
        >
          Device Management is unavailable for this session.
        </main>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <MdmRoute segment={segment} />
        </div>
      )}
    </AppLayout>
  )
}
